/**
 * Pure helpers for the FeedbackTriagePanel form. The component drives the
 * mutation and the render; this module owns the two computations whose
 * empty-string-versus-null behaviour is easy to get wrong:
 *
 *  - whether the form has unsaved changes (``isDirty``)
 *  - what payload to send to ``PUT /v1/admin/feedback/<id>`` (``buildSavePayload``)
 *
 * Co-located so a regression in either shows up under unit-test load rather
 * than only being caught by a manual triage round-trip.
 */

export interface TriageFormFields {
  /** Persisted ``admin_label`` from the server, or ``null`` if never set. */
  persistedLabel: string | null;
  /** Persisted ``admin_note`` from the server, or ``null`` if never set. */
  persistedNote: string | null;
  /** Current contents of the label input. */
  draftLabel: string;
  /** Current contents of the note textarea. */
  draftNote: string;
}

/**
 * ``true`` when either field's draft differs from what is persisted.
 *
 * Treats persisted ``null`` and an empty draft as equivalent: clearing a
 * never-set field is not a change.
 */
export function isDirty(fields: TriageFormFields): boolean {
  const labelChanged = fields.draftLabel !== (fields.persistedLabel ?? "");
  const noteChanged = fields.draftNote !== (fields.persistedNote ?? "");
  return labelChanged || noteChanged;
}

export interface AdminUpdateRequestBody {
  admin_label: string | null;
  admin_note: string | null;
}

/**
 * Build the ``request`` body for the admin update mutation.
 *
 * Backend contract: an absent key means "leave the column alone", ``null``
 * means "clear the column to NULL", a string means "overwrite the column".
 * The form has no "leave alone" affordance — it always rewrites both label
 * and note — so the only choice this helper makes is empty-string → ``null``
 * (so a cleared input becomes a NULL column rather than an empty string).
 */
export function buildSavePayload(fields: {
  draftLabel: string;
  draftNote: string;
}): AdminUpdateRequestBody {
  return {
    admin_label: fields.draftLabel.length > 0 ? fields.draftLabel : null,
    admin_note: fields.draftNote.length > 0 ? fields.draftNote : null,
  };
}
